/**
 * Shared presentational components.
 *
 * Small, domain-agnostic, and consistent in how they handle motion: every
 * transition is short, caused by the user, and disappears entirely under
 * prefers-reduced-motion (handled centrally in styles.css).
 */

import {
  CONFIRMATION_STATUS_LABELS,
  CONFIRMATION_STATUS_SHORT_LABELS,
  type ConfirmationStatus,
  type Priority,
} from '@lto/contracts';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Status — UI-004
// ---------------------------------------------------------------------------

/** Labels come from the shared contract; only the colour is decided here. */
const STATUS_COLOR: Record<ConfirmationStatus, string> = {
  DRAFT: 'var(--color-status-draft)',
  SENT: 'var(--color-status-sent)',
  RECEIVED: 'var(--color-status-received)',
  READ: 'var(--color-status-read)',
  REPLIED: 'var(--color-status-replied)',
  RESOLVED: 'var(--color-status-resolved)',
  CLOSED: 'var(--color-status-closed)',
  CANCELLED: 'var(--color-status-cancelled)',
};

/**
 * Colour never carries the meaning alone — the label is always present, so the
 * badge survives monochrome printing and colour-vision deficiency (UI-007).
 */
export function StatusBadge({
  status,
  full = false,
}: {
  status: ConfirmationStatus;
  /** Use the long label where there is room for it, such as a detail header. */
  full?: boolean;
}) {
  const color = STATUS_COLOR[status];
  const label = full
    ? CONFIRMATION_STATUS_LABELS[status]
    : CONFIRMATION_STATUS_SHORT_LABELS[status];
  return (
    <span
      className="animate-scale inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs font-medium whitespace-nowrap"
      title={CONFIRMATION_STATUS_LABELS[status]}
      style={{
        borderRadius: 'var(--radius-sm)',
        color,
        borderColor: 'color-mix(in srgb, currentColor 22%, transparent)',
        background: 'color-mix(in srgb, currentColor 6%, var(--color-surface))',
      }}
    >
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

const PRIORITY_LABEL: Record<Priority, string> = {
  NORMAL: 'Normal',
  PRIORITY: 'Priority',
  URGENT: 'Urgent',
};

/** Only non-normal priority is emphasised; flagging everything flags nothing. */
export function PriorityTag({ priority }: { priority: Priority }) {
  if (priority === 'NORMAL') {
    return <span style={{ color: 'var(--color-ink-subtle)' }}>{PRIORITY_LABEL[priority]}</span>;
  }
  const color = priority === 'URGENT' ? 'var(--color-danger)' : 'var(--color-warning)';
  return (
    <span className="inline-flex items-center gap-1 font-medium" style={{ color }}>
      <span aria-hidden="true" className="size-1 rounded-full" style={{ background: color }} />
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function Panel({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      className="animate-rise overflow-hidden border"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'var(--color-rule)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {title !== undefined && (
        <header
          className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3"
          style={{ borderColor: 'var(--color-rule)' }}
        >
          <div>
            <h2 className="text-md font-semibold tracking-tight" style={{ color: 'var(--color-ink)' }}>
              {title}
            </h2>
            {description !== undefined && (
              <p className="mt-0.5 text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
                {description}
              </p>
            )}
          </div>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A single figure.
 *
 * Restrained on purpose — §19 lists "giant statistics" among the things to
 * avoid. The number leads, but only just. It lifts very slightly on hover to
 * confirm it is a live surface rather than a printed panel.
 */
export function Metric({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'attention';
}) {
  return (
    <div
      className="interactive border px-4 py-3 hover:-translate-y-px"
      style={{
        background: 'var(--color-surface)',
        borderColor: tone === 'attention' ? 'var(--color-warning)' : 'var(--color-rule)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      <div
        className="text-xs font-medium uppercase tracking-wider"
        style={{ color: 'var(--color-ink-subtle)' }}
      >
        {label}
      </div>
      <div
        className="tabular mt-1.5 text-xl leading-none font-semibold"
        style={{ color: tone === 'attention' ? 'var(--color-warning)' : 'var(--color-ink)' }}
      >
        {value}
      </div>
      {hint !== undefined && (
        <div className="mt-1.5 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function Button({
  children,
  variant = 'secondary',
  loading = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost';
  loading?: boolean;
}) {
  const palette = {
    primary: {
      background: 'var(--color-accent)',
      color: 'var(--color-ink-inverse)',
      borderColor: 'var(--color-accent)',
    },
    secondary: {
      background: 'var(--color-surface)',
      color: 'var(--color-ink)',
      borderColor: 'var(--color-rule-strong)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--color-ink-muted)',
      borderColor: 'transparent',
    },
  }[variant];

  return (
    <button
      {...props}
      disabled={props.disabled === true || loading}
      className={`interactive inline-flex items-center justify-center gap-2 border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-55 ${props.className ?? ''}`}
      style={{ borderRadius: 'var(--radius-sm)', ...palette, ...props.style }}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Async states — UI-009
// ---------------------------------------------------------------------------

/**
 * Skeleton rows rather than a spinner.
 *
 * A skeleton shows the shape of what is coming, so the layout does not jump
 * when it arrives — and the wait reads as progress rather than as a stall.
 */
export function TableSkeleton({ rows = 5, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="px-4 py-3" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 py-2.5">
          {Array.from({ length: columns }, (_, colIndex) => (
            <div
              key={colIndex}
              className="skeleton h-3"
              style={{
                // Varied widths so it reads as content rather than as a grid.
                width: `${[18, 30, 16, 12, 10, 14][colIndex % 6]}%`,
                opacity: 1 - rowIndex * 0.12,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      className="animate-fade flex items-center justify-center gap-2 px-4 py-10 text-sm"
      style={{ color: 'var(--color-ink-subtle)' }}
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {label}…
    </div>
  );
}

/** Empty is not an error, and must not look like one. */
export function Empty({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="animate-rise px-4 py-12 text-center">
      <p className="text-md font-medium" style={{ color: 'var(--color-ink-muted)' }}>
        {title}
      </p>
      {detail !== undefined && (
        <p className="mx-auto mt-1.5 max-w-sm text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
          {detail}
        </p>
      )}
      {action !== undefined && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="animate-rise px-4 py-10 text-center" role="alert">
      <p className="text-sm font-medium" style={{ color: 'var(--color-danger)' }}>
        {message}
      </p>
      {onRetry !== undefined && (
        <div className="mt-3">
          <Button onClick={onRetry}>Try again</Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const TIMESTAMP = new Intl.DateTimeFormat('en-PH', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Manila',
});

export function formatTimestamp(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : TIMESTAMP.format(date);
}

/** Compact age, for "how long has this been sitting" columns. */
export function formatAge(value: string | null | undefined, now: Date = new Date()): string {
  if (value === null || value === undefined) return '—';
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '—';

  const minutes = Math.max(0, Math.round((now.getTime() - then.getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 === 0 ? `${hours}h` : `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return hours % 24 === 0 ? `${days}d` : `${days}d ${hours % 24}h`;
}
